/**
 * FeedbackDisplayPanel - Displays feedback text and metadata
 *
 * Shows the current feedback item with all contextual information
 * Requirement: ES-REQ-011, ES-REQ-012, ES-REQ-057
 */

import React from 'react';
import styled from 'styled-components';
import { useLabeling } from '@/context/labelingContext';

const Container = styled.div`
  background-color: var(--bg-card);
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const CompactNav = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  background-color: var(--bg-panel);
  border-bottom: 1px solid var(--color-neutral-200);
`;

const NavButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const NavButton = styled.button`
  padding: 4px 12px;
  font-size: 13px;
  font-weight: var(--font-weight-medium);
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: var(--bg-card);
  color: var(--text-dark);
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: #e8e8e8;
    border-color: var(--accent-feedback);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const PositionIndicator = styled.div`
  font-size: 13px;
  color: var(--text-muted);
  font-weight: var(--font-weight-medium);
`;

const ContentWrapper = styled.div`
  padding: 24px;
`;

const FeedbackText = styled.div`
  font-size: 18px;
  line-height: 1.6;
  color: var(--text-dark);
  padding: 20px;
  background-color: #f9f9f9;
  border-left: 4px solid var(--accent-feedback);
  border-radius: 4px;
  margin-bottom: 24px;
  min-height: 100px;
`;

const MetadataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-top: 20px;
`;

const MetadataItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MetadataLabel = styled.div`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MetadataValue = styled.div`
  font-size: var(--font-size-base);
  color: var(--text-dark);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: var(--text-muted);
  font-size: var(--font-size-xl);
`;

export const FeedbackDisplayPanel: React.FC = () => {
  const { state, previousFeedback, nextFeedback, skipFeedback } = useLabeling();
  const { feedback, currentIndex } = state;

  // Get current feedback item
  const currentFeedback = feedback[currentIndex];

  const isFirst = currentIndex === 0;
  const isLast = currentIndex === feedback.length - 1;
  const hasItems = feedback.length > 0;

  if (!currentFeedback) {
    return (
      <Container>
        <EmptyState>
          {feedback.length === 0
            ? 'No feedback items available to label'
            : 'Select a feedback item to begin labeling'}
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      {/* Compact navigation bar */}
      {hasItems && (
        <CompactNav>
          <NavButtons>
            <NavButton
              onClick={previousFeedback}
              disabled={isFirst || state.isLoading}
              title="Previous feedback item"
            >
              ← Prev
            </NavButton>
            <NavButton
              onClick={skipFeedback}
              disabled={isLast || state.isLoading}
              title="Skip this item"
            >
              Skip
            </NavButton>
            <NavButton
              onClick={nextFeedback}
              disabled={isLast || state.isLoading}
              title="Next feedback item"
            >
              Next →
            </NavButton>
          </NavButtons>
          <PositionIndicator>
            {currentIndex + 1} of {feedback.length}
          </PositionIndicator>
        </CompactNav>
      )}

      {/* Main content */}
      <ContentWrapper>
        <FeedbackText>
          "{currentFeedback.feedback}"
        </FeedbackText>

        <MetadataGrid>
          <MetadataItem>
            <MetadataLabel>Recommendation</MetadataLabel>
            <MetadataValue>{currentFeedback.recommendationName || 'Not specified'}</MetadataValue>
          </MetadataItem>

          <MetadataItem>
            <MetadataLabel>Practice Area</MetadataLabel>
            <MetadataValue>{currentFeedback.practiceArea || 'Not specified'}</MetadataValue>
          </MetadataItem>

          <MetadataItem>
            <MetadataLabel>Role</MetadataLabel>
            <MetadataValue>{currentFeedback.role || 'Not specified'}</MetadataValue>
          </MetadataItem>

          <MetadataItem>
            <MetadataLabel>District</MetadataLabel>
            <MetadataValue>{currentFeedback.district || 'Not specified'}</MetadataValue>
          </MetadataItem>

          <MetadataItem>
            <MetadataLabel>Location</MetadataLabel>
            <MetadataValue>{currentFeedback.location || 'Not specified'}</MetadataValue>
          </MetadataItem>

          <MetadataItem>
            <MetadataLabel>Submitted</MetadataLabel>
            <MetadataValue>
              {new Date(currentFeedback.timestamp).toLocaleDateString()}
            </MetadataValue>
          </MetadataItem>
        </MetadataGrid>
      </ContentWrapper>
    </Container>
  );
};

export default FeedbackDisplayPanel;
